import type { ModelConstraint, TradeConstraint } from '@/types/api/constraints'

import { TradeConstraintType } from '@moment/horizon-data-access/proposal'

import { Priority } from '@/portfolio/proposal-builder/types'

// Investor "Minimum quantity" <-> a single trade constraint per model. 0 = none.
// Match strictly on this field's signature (par basis, no upper bound) so we
// never read or clobber a trade constraint that isn't explicitly a Minimum
// trade quantity constraint.
function isMinimumQuantityConstraint(
  constraint: ModelConstraint,
): constraint is TradeConstraint {
  if (constraint.type !== TradeConstraintType.trade) return false
  const hasNoUpperBound =
    constraint.max_size === undefined || constraint.max_size === null
  return constraint.size_basis === 'par' && hasNoUpperBound
}

// Trade min_size from a constraint list; null = none.
export function readMinimumQuantity(
  constraints: ModelConstraint[],
): number | null {
  return constraints.find(isMinimumQuantityConstraint)?.min_size ?? null
}

// Upserts the trade constraint (non-positive removes it); reuses the
// existing id so identity is stable across edits.
export function applyMinimumQuantity(
  constraints: ModelConstraint[],
  minimumQuantity: number,
  makeId: () => string,
): ModelConstraint[] {
  const existing = constraints.find(isMinimumQuantityConstraint)
  const others = constraints.filter(
    (constraint) => !isMinimumQuantityConstraint(constraint),
  )

  if (!(minimumQuantity > 0)) {
    return others
  }

  const tradeConstraint: TradeConstraint = {
    id: existing?.id ?? makeId(),
    type: TradeConstraintType.trade,
    min_size: minimumQuantity,
    size_basis: 'par', // par (face) amount, not market value
    constraint_priority: Priority.High,
  }
  // explicitly omit max_size: no upper bound

  return [...others, tradeConstraint]
}
